import { ExpandMore, ExpandLess, Person as PersonIcon, Business as BusinessIcon } from "@mui/icons-material";
import { Box, Chip, Divider, IconButton, Link, Typography } from "@mui/material";
import React, { useState } from "react";
import { WorkStateEnum, WorkTypeEnum } from "../../store/types/work.types";
import { getWorkTypeColor, getWorkTypeIcon, getWorkTypeName } from "../../utils/workUtils";
import { getTeamColor } from "../../utils/workItemUtils";
import { formatTimeAgo } from "../../utils/dateFormatters";
import WorkStateIndicator from "../work-state-indicator/WorkStateIndicator";

export interface WorkListItemProps {
  workType: WorkTypeEnum;
  title: string;
  description?: string | null;
  customerName?: string;
  customerId?: string;
  state: WorkStateEnum;
  categoryName?: string;
  teamName?: string;
  teamId?: number;
  assignedToName?: string;
  modified?: string | null;
  onAction?: (e: React.MouseEvent<HTMLButtonElement>) => void;
  actionIcon?: React.ReactNode;
  actionTooltip?: string;
  showAction?: boolean;
  compact?: boolean;
}

const WorkListItem: React.FC<WorkListItemProps> = ({
  workType,
  title,
  description,
  customerName,
  state,
  categoryName,
  teamName,
  teamId,
  assignedToName,
  modified,
  onAction,
  actionIcon,
  actionTooltip = "Åtgärd",
  showAction = false,
  compact = false,
}) => {
  const [descriptionExpanded, setDescriptionExpanded] = useState(false);
  const typeColor = getWorkTypeColor(workType);

  const maxCharacters = 300;
  const maxLines = 5;
  const shouldTruncateByLines = description && description.split("\n").length > maxLines;
  const shouldTruncateByLength = description && description.length > maxCharacters;
  const shouldTruncate = shouldTruncateByLines || shouldTruncateByLength;

  let truncatedDescription = description;
  if (shouldTruncate && !descriptionExpanded && description) {
    const lines = description.split("\n").slice(0, maxLines);
    let result = lines.join("\n");
    if (result.length > maxCharacters) {
      result = result.substring(0, maxCharacters).trim();
      const lastSpace = result.lastIndexOf(" ");
      if (lastSpace > maxCharacters * 0.8) {
        result = result.substring(0, lastSpace);
      }
    }
    truncatedDescription = result;
  }

  // Compact layout — single row
  if (compact) {
    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 2,
          flexWrap: { xs: "wrap", md: "nowrap" },
        }}
      >
        {/* Type icon */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            bgcolor: `${typeColor}20`,
            color: typeColor,
            width: 36,
            height: 36,
            borderRadius: 1,
            flexShrink: 0,
          }}
        >
          {getWorkTypeIcon(workType)}
        </Box>

        {/* Name + type */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            variant="subtitle2"
            sx={{
              fontWeight: 600,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {title}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {getWorkTypeName(workType)}
          </Typography>
        </Box>

        {/* Customer */}
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{
            minWidth: 120,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {customerName || "Ingen kund"}
        </Typography>

        {/* State */}
        <WorkStateIndicator state={state} compact />

        {/* Category */}
        {categoryName && (
          <Chip
            label={categoryName}
            size="small"
            variant="outlined"
            sx={{ fontSize: "0.7rem", height: 22, display: { xs: "none", md: "flex" } }}
          />
        )}

        {/* Date */}
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ minWidth: 80, textAlign: "right", display: { xs: "none", sm: "block" } }}
        >
          {formatTimeAgo(modified ?? null)}
        </Typography>

        {/* Assigned to */}
        <Box
          sx={{
            display: { xs: "none", lg: "flex" },
            alignItems: "center",
            minWidth: 120,
          }}
        >
          <PersonIcon fontSize="small" color="action" sx={{ mr: 0.5 }} />
          <Typography variant="caption" color="text.secondary">
            {assignedToName || "Ej tilldelad"}
          </Typography>
        </Box>

        {/* Action */}
        {showAction && onAction && (
          <IconButton
            size="small"
            onClick={onAction}
            title={actionTooltip}
            sx={{ width: 28, height: 28 }}
          >
            {actionIcon}
          </IconButton>
        )}
      </Box>
    );
  }

  // Default (card content) layout
  return (
    <Box>
      <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            bgcolor: `${typeColor}20`,
            color: typeColor,
            width: 32,
            height: 32,
            borderRadius: 1,
            mr: 1.5,
            border: `2px solid ${typeColor}40`,
          }}
        >
          {getWorkTypeIcon(workType)}
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            variant="subtitle1"
            sx={{
              fontWeight: 600,
              fontSize: "0.95rem",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {title}
          </Typography>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ fontSize: "0.8rem" }}
            >
              {getWorkTypeName(workType)}
            </Typography>
            <WorkStateIndicator state={state} compact />
          </Box>
        </Box>

        {/* Action button top-right */}
        {showAction && onAction && (
          <IconButton
            size="small"
            onClick={onAction}
            title={actionTooltip}
            sx={{
              width: 28,
              height: 28,
              bgcolor: "white",
              "&:hover": { bgcolor: "grey.100" },
            }}
          >
            {actionIcon}
          </IconButton>
        )}
      </Box>

      {description && (
        <Box sx={{ mb: 2 }}>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{
              fontSize: "0.85rem",
              whiteSpace: "pre-wrap",
              ...(!descriptionExpanded && {
                display: "-webkit-box",
                WebkitLineClamp: 2,
                WebkitBoxOrient: "vertical",
                overflow: "hidden",
              }),
            }}
          >
            {shouldTruncate && !descriptionExpanded ? truncatedDescription + "..." : description}
          </Typography>
          {shouldTruncate && (
            <Link
              component="button"
              variant="caption"
              onClick={(e) => {
                e.stopPropagation();
                setDescriptionExpanded(!descriptionExpanded);
              }}
              sx={{
                mt: 0.5,
                display: "inline-flex",
                alignItems: "center",
                gap: 0.5,
                textDecoration: "none",
                "&:hover": { textDecoration: "underline" },
              }}
            >
              {descriptionExpanded ? (
                <>Visa mindre <ExpandLess fontSize="small" /></>
              ) : (
                <>Läs mer <ExpandMore fontSize="small" /></>
              )}
            </Link>
          )}
        </Box>
      )}

      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 2 }}>
        {categoryName && (
          <Chip
            label={categoryName}
            size="small"
            variant="outlined"
            sx={{ fontSize: "0.75rem", height: 24 }}
          />
        )}
        <Chip
          icon={<BusinessIcon fontSize="small" />}
          label={teamName || "Ej tilldelat"}
          size="small"
          variant="outlined"
          sx={{
            borderColor: getTeamColor(teamId),
            color: getTeamColor(teamId),
            fontSize: "0.75rem",
            height: 24,
          }}
        />
      </Box>

      <Divider sx={{ my: 1 }} />

      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mt: 1,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center" }}>
          <PersonIcon fontSize="small" color="action" />
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ ml: 0.5, fontSize: "0.8rem" }}
          >
            {assignedToName || "Ej tilldelad"}
          </Typography>
        </Box>
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ fontSize: "0.75rem" }}
        >
          Uppdaterad {formatTimeAgo(modified ?? null)}
        </Typography>
      </Box>
    </Box>
  );
};

export default WorkListItem;
